/**
 * Kampanya paneli sayaci icin gorsel efektler.
 *
 * `countdown-timer` elementi rakamlari her saniye `textContent` ile yeniden
 * yaziyor; bu element de:
 *  - rakam gercekten degistiginde kisa bir kayma/fade animasyonu oynatir
 *    (her tick'te tum birimler yeniden yazildigi icin son deger hafizada tutulur),
 *  - alt kenardaki ilerleme cizgisinin CSS animasyonunu icinde bulunulan
 *    dakikaya faz olarak sabitler.
 *
 * Hareket azaltma tercihi aciksa hicbir animasyon baglanmaz.
 */

const UNIT_REFS = ["day", "hour", "minute", "second"];
const TICK_DURATION = 220;

class CampaignTimerFx extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #observer;

  /** @type {Map<Element, string>} */
  #lastValues = new Map();

  connectedCallback() {
    const timer = this.closest("countdown-timer") ?? this.parentElement;
    if (!timer) return;

    this.#syncProgressPhase(timer);

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const values = UNIT_REFS.map((unit) => timer.querySelector(`[ref="${unit}"]`)).filter(
      /** @returns {node is Element} */ (node) => node instanceof Element,
    );

    if (!values.length) return;

    for (const node of values) {
      this.#lastValues.set(node, node.textContent ?? "");
    }

    this.#observer = new MutationObserver((records) => {
      const changed = new Set();

      for (const record of records) {
        const node = values.find((value) => value === record.target || value.contains(record.target));
        if (node) changed.add(node);
      }

      for (const node of changed) {
        const next = node.textContent ?? "";
        if (this.#lastValues.get(node) === next) continue;
        this.#lastValues.set(node, next);
        this.#playTick(node);
      }
    });

    for (const node of values) {
      this.#observer.observe(node, { childList: true, characterData: true, subtree: true });
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = undefined;
    this.#lastValues.clear();
  }

  /**
   * Ilerleme cizgisini gecerli dakikanin gecen saniyesi kadar geriden baslatir.
   *
   * @param {Element} timer
   */
  #syncProgressPhase(timer) {
    const progress = timer.querySelector(".campaign-timer__progress");
    if (!(progress instanceof HTMLElement)) return;

    const now = new Date();
    const elapsed = now.getSeconds() + now.getMilliseconds() / 1000;
    progress.style.setProperty("--cp-phase", `-${elapsed.toFixed(2)}s`);
  }

  /**
   * @param {Element} node
   */
  #playTick(node) {
    if (typeof node.animate !== "function") return;

    node.animate(
      [
        { opacity: 0.35, transform: "translateY(-0.14em)" },
        { opacity: 1, transform: "translateY(0)" },
      ],
      { duration: TICK_DURATION, easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
    );
  }
}

if (!customElements.get("campaign-timer-fx")) {
  customElements.define("campaign-timer-fx", CampaignTimerFx);
}
